import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@material-ui/core";
import SwitchField from "components/Forms/SwitchField";
import ButtonWithLoading from "components/ButtonWithLoading";
import { GroupLabel, PermissionItem, StyledDivider } from "./styled";

import { PermissionGroup, PermissionsItem } from "./types";

interface Props {
  roleName: string;
  permissions: PermissionsItem[];
  isOpen: boolean;
  isSaving: boolean;
  onClose: () => void;
  onSave: (changes: Record<string, boolean>) => Promise<void>;
}

export default function RoleFormModal(props: Props) {
  const { t } = useTranslation("users");
  const { roleName, permissions, isOpen, onClose, onSave } = props;
  const [form, setForm] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(permissions.map(p => [p.name, p.value]))
  );

  function handleToggle(name: string) {
    return () => {
      setForm(previous => ({ ...previous, [name]: !previous[name] }));
    };
  }

  async function handleSave() {
    await onSave(form);
    onClose();
  }

  const permissionGroups = [...getPermissionsByGroups(permissions)];

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth>
      <DialogTitle>
        {t("editRoleModal.title", { role: t(roleName) })}
      </DialogTitle>
      <DialogContent style={{ paddingTop: "8px" }}>
        {permissionGroups.map(([group, groupPermissions], groupIndex) => (
          <div key={group ?? "ungrouped"}>
            {groupIndex > 0 && <StyledDivider />}
            {group && (
              <GroupLabel variant="overline" color="textSecondary">
                {t(`rolePermissionGroup.${group}`)}
              </GroupLabel>
            )}
            {groupPermissions.map(permission => (
              <PermissionItem key={permission.name}>
                <SwitchField
                  name={permission.name}
                  label={t(permission.label)}
                  description={t(permission.description)}
                  value={form[permission.name]}
                  onChange={handleToggle(permission.name)}
                />
              </PermissionItem>
            ))}
          </div>
        ))}
      </DialogContent>
      <DialogActions>
        <Button
          color="default"
          data-testid="roles__cancel-edit"
          disableElevation
          onClick={onClose}
        >
          {t("common:cancel")}
        </Button>
        <ButtonWithLoading
          loading={props.isSaving}
          color="primary"
          data-testid="roles__save-role-btn"
          disableElevation
          onClick={handleSave}
          variant="contained"
        >
          {t("common:save")}
        </ButtonWithLoading>
      </DialogActions>
    </Dialog>
  );
}

function getPermissionsByGroups(permissions: PermissionsItem[]) {
  return permissions.reduce((acc, permission) => {
    const current = acc.get(permission.group) || [];
    acc.set(permission.group, [...current, permission]);
    return acc;
  }, new Map<PermissionGroup | undefined, PermissionsItem[]>());
}
